import { ArrowRightAlt, HourglassTop } from '@mui/icons-material'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import ArrowBack from '@mui/icons-material/ArrowBack'
import DoDisturb from '@mui/icons-material/DoDisturb'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { Alert, Fade, Grid } from '@mui/material'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import Input from '@mui/material/Input'
import InputAdornment from '@mui/material/InputAdornment'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { Box } from '@mui/system'
import { ROUTE_TO_LOGIN } from 'api/axios'
import Logo from 'common/components/Logo'
import { NotificationsContext } from 'features/notifications/providers/NotificationsProvider'
import { selectPortal } from 'features/portal/portalSlice'
import { userLogin } from 'features/users/stores/userSlice'
import i18n from 'lang/idiomas'
import { enqueueSnackbar } from 'notistack'
import { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import ThemeManager from 'theming/ThemeManager'
import PasswordController from '../lib/PasswordController'

/**
 * Callback for adding two numbers.
 * a
 * @callback loadingParentCallback
 * @param {boolean} state - The loading state for the parent.
 */

/**
 * Formulario de inicio de sesión
 *
 * @param {Object} params - parametros del componente
 * @param {loadingParentCallback} [params.loadingParentCallback] - callback para cambiar el estado del componente
 *
 * @returns {React.ReactElement} - un componente para iniciar sesión
 */
const LoginForm = ({ loadingParentCallback = undefined }) => {
  const dispatch = useDispatch()
  const { forceRefresh } = useContext(NotificationsContext)
  const PREVIOUS_ROUTE = sessionStorage.getItem(ROUTE_TO_LOGIN)

  const portal = useSelector(selectPortal)

  const userRef = useRef()
  const passwordRef = useRef()

  const navigate = useNavigate()
  const [lockout, setLockout] = useState(false)
  const [step, setStep] = useState(1)
  const containerRef = useRef(null)

  useEffect(() => {
    // @ts-ignore
    if (userRef.current) userRef.current.focus()
  }, [])

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loginRequestStatus, setLoginRequestStatus] = useState('idle')

  const [showPassword, setShowPassword] = useState(false)

  const handleClickShowPassword = () => setShowPassword((show) => !show)

  const handleMouseDownPassword = (e) => e.preventDefault()

  const onUsernameChanged = (e) => {
    setUsername(e.target.value)
    if (lockout) setLockout(false)
  }
  const onPasswordChanged = (e) => setPassword(e.target.value)
  const [positionUsername, setPositionUsername] = useState('relative')
  const [positionPassword, setPositionPassword] = useState('absolute')
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showPwdRecovery, setShowPwdRecovery] = useState(false)

  useEffect(() => {
    if (step === 1) {
      setPositionUsername('relative')
      setPositionPassword('absolute')
    }
    if (step === 2) {
      setPositionPassword('relative')
      setPositionUsername('absolute')
      // Hacer autofocus en el campo de contraseña cuando se muestra
      setTimeout(() => {
        if (passwordRef.current) {
          passwordRef.current.focus()
        }
      }, 100)
    }
  }, [step])

  const canLogin = [username].every(Boolean) && loginRequestStatus === 'idle'

  const checkLocked = async () => {
    setLoading(true)
    try {
      const hasToResetPassword = await PasswordController.hasToResetPassword(username)
      setShowPwdRecovery(hasToResetPassword.show_pwd_recovery)
      if (hasToResetPassword.must_change) {
        setLockout(true)
        enqueueSnackbar(
          i18n.t('login.snackccn', {
            defaultValue:
              'Es necesario que cambies tu contraseña, por favor verifica tu correo para continuar',
          }),
          {
            variant: 'error',
            autoHideDuration: 10000,
          },
        )
        setLoading(false)
        setErrorMessage(
          <Box>
            <Typography fontWeight="bolder">
              {i18n.t('login.usrblckd', { defaultValue: 'El usuario está bloqueado.' })}
            </Typography>
            <Typography>
              {i18n.t('login.txtccn', {
                defaultValue:
                  'Es necesario que cambies tu contraseña, por favor verifica tu correo para continuar.',
              })}
            </Typography>
          </Box>,
        )
        return
      }
      setStep(2)
      setLockout(false)
    } catch (error) {
      setStep(2)
      setLockout(false)
    }
    setLoading(false)
  }

  const onLoginClicked = async () => {
    if (canLogin && !lockout) {
      try {
        setLoginRequestStatus('pending')

        // @ts-ignore
        await dispatch(userLogin({ username, password })).unwrap()
        if (loadingParentCallback) {
          loadingParentCallback(true)
        }

        setUsername('')
        setPassword('')
        forceRefresh()

        navigate('/')
      } catch (err) {
        setErrorMessage(
          i18n.t('login.usrpaser', { defaultValue: 'Usuario o contraseña incorrectos' }),
        )
        if (loadingParentCallback) {
          loadingParentCallback(false)
        }
      } finally {
        setLoginRequestStatus('idle')
      }
    }
  }
  const themeManager = new ThemeManager()

  const logoOutside = themeManager.options.loginLogoOutside ?? false
  const apply = themeManager.options.apply ?? false

  return (
    <Box
      className="inicio"
      sx={{
        minWidth: {
          md: '50%',
        },
        width: {
          xs: '100%',
          md: '60%',
        },
        maxWidth: {
          md: '60%',
        },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 80%',
      }}
    >
      {logoOutside ? (
        <Logo
          logoType="Large"
          sx={{
            mb: 5,
            transfor: 'translateX(-30%)',
          }}
        />
      ) : (
        <></>
      )}
      {apply ? (
        <>
          <Logo
            logoType="Large"
            sx={{
              m: {
                xs: 'auto',
                md: '10px 0px 50px 0px',
              },
              my: 5,
              width: {
                xs: '70%',
                md: '30%',
              },
            }}
          />
          <Typography
            variant="h4"
            sx={{
              fontWeight: '100',
              color: 'primary.main',
              mt: { xs: 2, md: 4 },
              mb: { xs: 3, md: 5 },
              textAlign: { xs: 'center', md: 'unset' },
              fontSize: {
                xs: '1.75rem',
              },
            }}
          >
            {portal?.portal?.descripcion ?? 'Portal del Empleado'}
          </Typography>
        </>
      ) : (
        <></>
      )}
      <Paper
        className="inicio"
        sx={{
          backgroundColor: 'background.paper',
          color: 'text.primary',
          p: { xs: 2, sm: 3, md: 4 },
          width: { md: '100%' },
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {!logoOutside && !apply ? (
            <Logo
              logoType="Large"
              sx={{
                width: '100%',
                height: '100%',
                mb: 2,
              }}
            />
          ) : (
            <></>
          )}
        </div>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <Typography variant="h6" sx={{ m: 2 }} align="center">
            {step === 1
              ? i18n.t('login.titusuar', { defaultValue: 'NOMBRE DE USUARIO' })
              : i18n.t('login.titpassw', { defaultValue: 'CONTRASEÑA' })}
          </Typography>
        </div>

        <Box ref={containerRef} sx={{ transition: '.3s', width: '100%', position: 'relative' }}>
          <Fade in={step === 1}>
            <div
              style={{
                position: positionUsername,
                top: 0,
              }}
            >
              <FormControl sx={{ width: '100%' }} variant="standard">
                <Input
                  autoFocus
                  id="standard-adornment-username"
                  inputProps={{
                    tabIndex: 1,
                    placeholder: i18n.t('login.pholdusu', { defaultValue: 'Usuario' }),
                  }}
                  type="text"
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        tabIndex={-1}
                        aria-label="icono usuario"
                        sx={{ color: 'text.primary' }}
                      >
                        <AccountCircleOutlinedIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && username) {
                      checkLocked()
                    }
                  }}
                  autoComplete="false"
                  ref={userRef}
                  value={username}
                  onChange={onUsernameChanged}
                />
              </FormControl>

              {/* <FormGroup sx={{ mt: 1 }}>
                <FormControlLabel
                  disabled={lockout}
                  control={<Checkbox />}
                  label="Manterner conectado"
                />
              </FormGroup> */}
              <Button
                endIcon={loading ? <HourglassTop /> : !lockout ? <ArrowRightAlt /> : <DoDisturb />}
                variant="contained"
                sx={{
                  width: '100%',
                  mt: 2,
                }}
                onClick={checkLocked}
                disabled={lockout || !canLogin}
              >
                {i18n.t('login.btnnext', { defaultValue: 'Siguiente' })}
              </Button>
            </div>
          </Fade>
          <Fade in={step === 2}>
            <div
              style={{
                width: '100%',
                position: positionPassword,
                top: 0,
              }}
            >
              <FormControl sx={{ width: '100%' }} variant="standard">
                <Input
                  id="standard-adornment-password"
                  type={showPassword ? 'text' : 'password'}
                  inputRef={passwordRef}
                  inputProps={{ tabIndex: 2, placeholder: 'Contraseña' }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      onLoginClicked()
                    }
                  }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        sx={{ color: 'text.primary' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  value={password}
                  onChange={onPasswordChanged}
                />
              </FormControl>
              {showPwdRecovery ? (
                <Button
                  sx={{ display: 'block', mt: 2, color: 'text.primary' }}
                  LinkComponent={Link}
                  disabled={lockout}
                  to="/login"
                  state={{ recovery: true }}
                >
                  {i18n.t('login.recpass', { defaultValue: 'Recuperar contraseña' })}
                </Button>
              ) : null}
              <Grid container spacing={0} alignItems="center" sx={{ mt: 1 }}>
                <Grid size={2}>
                  <IconButton
                    onClick={() => {
                      setStep(1)
                    }}
                    sx={{ color: 'text.primary' }}
                  >
                    <ArrowBack />
                  </IconButton>
                </Grid>
                <Grid size={10}>
                  <Button
                    variant="contained"
                    sx={{
                      width: '100%',
                    }}
                    onClick={onLoginClicked}
                    disabled={lockout || !canLogin}
                  >
                    {i18n.t('login.enter', { defaultValue: 'Entrar' })}
                  </Button>
                </Grid>
              </Grid>
            </div>
          </Fade>
        </Box>
        {errorMessage ? (
          <Alert
            sx={{ mt: 2, minWidth: '25ch', width: '100%' }}
            variant="outlined"
            severity="error"
          >
            {errorMessage}
          </Alert>
        ) : (
          <></>
        )}
      </Paper>
    </Box>
  )
}

export default LoginForm
